import { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useOnboardingStatus } from "@/hooks/hr/training/use-trainings";
import { usePermissions } from "@/contexts/permission-context";

/**
 * Banner shown below the header when the user has pending mandatory trainings.
 * Dismissable per session (reappears on next login).
 * Skipped for admins (they manage trainings, not complete them).
 */
export const PendingTrainingBanner = () => {
  const { isAdmin } = usePermissions();
  const [dismissed, setDismissed] = useState(false);
  const { data: status, isLoading } = useOnboardingStatus();

  // Don't show for admins, while loading, if dismissed, or if everything is complete
  if (isAdmin || isLoading || dismissed || !status || status.is_complete) {
    return null;
  }

  const pending = status.total_required - status.completed_count;

  return (
    <div className="shrink-0 border-b border-border bg-card px-4 py-2">
      <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
        <GraduationCap className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800 dark:text-amber-400">
          {pending === 1
            ? "Tem 1 formação obrigatória pendente"
            : `Tem ${pending} formações obrigatórias pendentes`}
        </AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span className="text-sm text-amber-700 dark:text-amber-300">
            Completa as formações atribuídas ao teu departamento para continuar.
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild className="border-amber-500/50 text-amber-800 hover:bg-amber-100 dark:text-amber-300">
              <Link to="/dashboard/hr/my-trainings">Ver formações</Link>
            </Button>
            <Button
              aria-label="Dispensar aviso"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-amber-600 hover:text-amber-800"
              onClick={() => setDismissed(true)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};
